import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Calendar, MessageSquare, Image as ImageIcon, LayoutDashboard, CheckCircle, XCircle, Clock, Upload, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [gallery, setGallery] = useState([]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/stats`, { withCredentials: true });
      setStats(res.data);
    } catch (e) { console.error(e); }
  }, []);

  const fetchBookings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/bookings`, { withCredentials: true });
      setBookings(res.data);
    } catch (e) { console.error(e); }
  }, []);

  const fetchContacts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/contacts`, { withCredentials: true });
      setContacts(res.data);
    } catch (e) { console.error(e); }
  }, []);

  const fetchGallery = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/gallery`);
      setGallery(res.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    if (!loading) {
      if (!user || !user.is_admin) {
        navigate('/admin/login', { replace: true });
      }
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user?.is_admin) {
      fetchStats();
      fetchBookings();
      fetchContacts();
      fetchGallery();
    }
  }, [user, fetchStats, fetchBookings, fetchContacts, fetchGallery]);

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      await axios.patch(
        `${API}/admin/bookings/${bookingId}/status`,
        { status: newStatus },
        { withCredentials: true }
      );
      toast.success('Stato aggiornato');
      fetchBookings();
      fetchStats();
    } catch (e) {
      toast.error('Errore aggiornamento stato');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', 'general');

    try {
      await axios.post(`${API}/admin/gallery/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Immagine caricata con successo');
      fetchGallery();
    } catch (err) {
      toast.error('Errore caricamento immagine');
    }
  };

  const deleteImage = async (imageId) => {
    if (!window.confirm('Eliminare questa immagine?')) return;
    try {
      await axios.delete(`${API}/admin/gallery/${imageId}`, { withCredentials: true });
      toast.success('Immagine eliminata');
      fetchGallery();
    } catch (e) {
      toast.error('Errore eliminazione');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login', { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F6F0]">
        <p className="text-[#666666]">Caricamento...</p>
      </div>
    );
  }

  if (!user?.is_admin) {
    return null;
  }

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800',
    confirmed: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800'
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F9F6F0]">
      {/* Admin Header */}
      <header className="bg-white border-b border-[#2C5263]/10 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <ShieldBadge />
              <h1 className="text-xl font-serif text-[#2C5263] font-bold">Admin Panel</h1>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                {user.picture && (
                  <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                )}
                <span className="text-sm text-[#1A1A1A] hidden sm:inline" data-testid="admin-user-name">
                  {user.name}
                </span>
              </div>
              <button
                data-testid="admin-logout-button"
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 text-sm text-[#666666] hover:text-[#2C5263] transition-colors"
              >
                <LogOut size={16} />
                <span className="hidden sm:inline">{t('admin.logout')}</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto">
          {[
            { id: 'dashboard', label: t('admin.dashboard'), icon: LayoutDashboard },
            { id: 'bookings', label: t('admin.bookings'), icon: Calendar },
            { id: 'contacts', label: t('admin.contacts'), icon: MessageSquare },
            { id: 'gallery', label: t('admin.gallery'), icon: ImageIcon }
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                data-testid={`admin-tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'bg-[#2C5263] text-white'
                    : 'bg-white text-[#666666] hover:text-[#2C5263]'
                }`}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Dashboard */}
        {activeTab === 'dashboard' && stats && (
          <div data-testid="stats-panel" className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Prenotazioni Totali" value={stats.total_bookings} icon={Calendar} />
            <StatCard label="In Attesa" value={stats.pending_bookings} icon={Clock} color="yellow" />
            <StatCard label="Confermate" value={stats.confirmed_bookings} icon={CheckCircle} color="green" />
            <StatCard label="Messaggi" value={stats.total_contacts} icon={MessageSquare} />
          </div>
        )}

        {/* Bookings */}
        {activeTab === 'bookings' && (
          <div data-testid="bookings-panel" className="bg-white rounded-lg border border-[#2C5263]/10 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#F9F6F0]">
                  <tr className="text-left text-sm text-[#666666]">
                    <th className="p-4">Ospite</th>
                    <th className="p-4">Appartamento</th>
                    <th className="p-4">Date</th>
                    <th className="p-4">Ospiti</th>
                    <th className="p-4">Stato</th>
                    <th className="p-4">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.length === 0 ? (
                    <tr><td colSpan="6" className="p-8 text-center text-[#666666]">Nessuna prenotazione</td></tr>
                  ) : bookings.map((b) => (
                    <tr key={b.id} data-testid={`booking-row-${b.id}`} className="border-t border-[#2C5263]/5">
                      <td className="p-4">
                        <div className="text-[#1A1A1A] font-medium">{b.guest_name}</div>
                        <div className="text-xs text-[#666666]">{b.guest_email}</div>
                        <div className="text-xs text-[#666666]">{b.guest_phone}</div>
                      </td>
                      <td className="p-4 text-[#1A1A1A]">{b.property_name}</td>
                      <td className="p-4 text-sm">
                        <div>{b.check_in}</div>
                        <div className="text-[#666666]">→ {b.check_out}</div>
                      </td>
                      <td className="p-4 text-[#1A1A1A]">{b.guests}</td>
                      <td className="p-4">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${statusColors[b.status]}`}>
                          {b.status}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex gap-2">
                          {b.status !== 'confirmed' && (
                            <button
                              data-testid={`confirm-booking-${b.id}`}
                              onClick={() => updateBookingStatus(b.id, 'confirmed')}
                              className="text-green-600 hover:text-green-800"
                              title="Conferma"
                            >
                              <CheckCircle size={20} />
                            </button>
                          )}
                          {b.status !== 'cancelled' && (
                            <button
                              data-testid={`cancel-booking-${b.id}`}
                              onClick={() => updateBookingStatus(b.id, 'cancelled')}
                              className="text-red-600 hover:text-red-800"
                              title="Annulla"
                            >
                              <XCircle size={20} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Contacts */}
        {activeTab === 'contacts' && (
          <div data-testid="contacts-panel" className="space-y-4">
            {contacts.length === 0 ? (
              <div className="bg-white p-8 rounded-lg text-center text-[#666666]">Nessun messaggio</div>
            ) : contacts.map((c) => (
              <div key={c.id} data-testid={`contact-row-${c.id}`} className="bg-white p-6 rounded-lg border border-[#2C5263]/10">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="font-semibold text-[#1A1A1A]">{c.name}</h3>
                    <p className="text-sm text-[#666666]">{c.email} · {c.phone || 'N/A'}</p>
                  </div>
                  <span className="text-xs text-[#666666]">
                    {new Date(c.created_at).toLocaleDateString('it-IT')}
                  </span>
                </div>
                <p className="text-[#1A1A1A] mt-3 whitespace-pre-wrap">{c.message}</p>
              </div>
            ))}
          </div>
        )}

        {/* Gallery */}
        {activeTab === 'gallery' && (
          <div data-testid="gallery-panel">
            <div className="mb-6">
              <label
                data-testid="gallery-upload-button"
                className="inline-flex items-center gap-2 bg-[#2C5263] text-white px-6 py-3 rounded-md text-sm font-medium hover:bg-[#1A3644] transition-colors cursor-pointer"
              >
                <Upload size={16} />
                Carica Immagine
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  data-testid="gallery-upload-input"
                />
              </label>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {gallery.length === 0 ? (
                <p className="text-[#666666] col-span-full">Nessuna immagine caricata</p>
              ) : gallery.map((img) => (
                <div key={img.id} data-testid={`gallery-item-${img.id}`} className="bg-white rounded-lg border border-[#2C5263]/10 overflow-hidden">
                  <img
                    src={`${API}/gallery/${img.id}/download`}
                    alt={img.original_filename}
                    className="w-full aspect-square object-cover"
                  />
                  <div className="p-2 flex justify-between items-center">
                    <span className="text-xs text-[#666666] truncate">{img.category}</span>
                    <button
                      data-testid={`delete-image-${img.id}`}
                      onClick={() => deleteImage(img.id)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ label, value, icon: Icon, color = 'default' }) => {
  const colors = {
    default: 'text-[#2C5263]',
    yellow: 'text-yellow-600',
    green: 'text-green-600'
  };
  return (
    <div className="bg-white p-6 rounded-lg border border-[#2C5263]/10">
      <div className="flex items-center justify-between mb-3">
        <Icon size={24} className={colors[color]} />
      </div>
      <div className="text-3xl font-serif text-[#1A1A1A]">{value}</div>
      <div className="text-sm text-[#666666] mt-1">{label}</div>
    </div>
  );
};

const ShieldBadge = () => (
  <div className="w-8 h-8 bg-[#2C5263] rounded-md flex items-center justify-center">
    <span className="text-white text-xs font-bold">CT</span>
  </div>
);

export default AdminDashboardPage;
